import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ArticleSchema, ArticleMeta } from '~/components/seo-content'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { COMPETITORS, AFFICHESERVICES } from '~/lib/competitors-data'
import { ProgressivePicture } from '~/components/progressive-picture'
import { CheckCircle, ArrowRight, Shield } from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const { competitor: slug } = params
  const competitor = slug ? COMPETITORS[slug] : null
  if (!competitor) throw new Response('Alternative non trouvée', { status: 404 })
  return { competitor }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return [{ title: 'Alternative non trouvée' }]
  const c = data.competitor
  return [
    { title: `Alternative à ${c.name} en 2026 — AfficheServices` },
    { name: 'description', content: `Vous cherchez une alternative à ${c.name} ? AfficheServices : 110 000+ artisans, vérification 3 niveaux, filtre RGE, médiation gratuite. Comparatif détaillé + 3 devis gratuits.`.slice(0, 160) },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/alternatives/${c.slug}` },
    { property: 'og:title', content: `Alternative à ${c.name} — AfficheServices` },
    { property: 'og:type', content: 'website' },
  ]
}

export default function AlternativePage() {
  const { competitor: c } = useLoaderData<typeof loader>()

  const faqs = [
    {
      question: `Pourquoi chercher une alternative à ${c.name} ?`,
      answer: c.switchingReasons.join(' ') + ` AfficheServices répond à ces besoins avec ${AFFICHESERVICES.proCount.toLowerCase()}, une vérification en 3 niveaux et un filtre RGE natif.`,
    },
    {
      question: `Le service AfficheServices est-il vraiment 100 % gratuit ?`,
      answer: `Oui, AfficheServices est 100 % gratuit pour les particuliers, sans inscription ni engagement. Notre modèle économique repose sur les abonnements et leads achetés par les artisans Pro et Enterprise — jamais sur les clients.`,
    },
    {
      question: `Combien d'artisans sont vérifiés sur AfficheServices ?`,
      answer: `Plus de 110 000 artisans sont référencés en France. Chaque profil passe 3 contrôles : vérification SIRET via SIRENE (INSEE), contrôle de l'assurance responsabilité civile professionnelle, et vérification d'identité (KYC) pour les comptes Pro et Enterprise.`,
    },
    {
      question: `Puis-je filtrer par certification RGE ?`,
      answer: `Oui, AfficheServices propose un filtre RGE / Qualibat / Qualifelec / Qualit'EnR activable directement sur les pages catégorie. Indispensable pour bénéficier de MaPrimeRénov', CEE et éco-PTZ qui peuvent couvrir jusqu'à 60 % des travaux énergétiques.`,
    },
    {
      question: `Que se passe-t-il en cas de litige avec un artisan ?`,
      answer: `AfficheServices propose une médiation gratuite. Notre équipe contacte l'artisan pour trouver une solution amiable. Si le litige persiste, nous orientons vers les organismes compétents (Médiateur de la consommation, DGCCRF). Sur ${c.name}, le processus de médiation n'est pas documenté publiquement.`,
    },
    {
      question: `Combien de temps pour recevoir les devis ?`,
      answer: `3 devis sous 48 heures pour une demande standard. Pour les urgences (fuite, panne électrique, serrure bloquée), réponse sous 1 à 4 heures grâce à notre réseau d'artisans 24/7 pour la plomberie, l'électricité et la serrurerie.`,
    },
  ]

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Alternatives', url: 'https://www.afficheservices.fr/alternatives' },
        { name: `Alternative à ${c.name}`, url: `https://www.afficheservices.fr/alternatives/${c.slug}` },
      ]} />
      <ArticleSchema
        title={`Alternative à ${c.name} en 2026`}
        description={`Comparatif complet entre AfficheServices et ${c.name} : vérification, filtre RGE, médiation, prix. 110 000+ artisans vérifiés.`}
        url={`https://www.afficheservices.fr/alternatives/${c.slug}`}
      />
      <FAQSchema items={faqs} />

      <article className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        {/* Hero with background image — provides indexable alt text + visual context */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-8 text-white">
          <div className="pointer-events-none absolute inset-0 opacity-15">
            <ProgressivePicture
              baseUrl="https://images.unsplash.com/photo-1556157382-97eda2d62296"
              alt={`Comparatif AfficheServices et ${c.name} : alternative à ${c.name} pour devis travaux`}
              width={1200}
              height={400}
              priority
              sizes="(max-width: 768px) 100vw, 1024px"
              className="h-full"
            />
          </div>
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-200">Comparatif 2026</p>
            <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Alternative à {c.name}</h1>
            <p className="mt-3 text-sm leading-relaxed text-blue-100 sm:text-base">
              Vous cherchez une alternative à {c.name} ? AfficheServices propose 110 000+ artisans
              vérifiés, un filtre RGE natif et une médiation gratuite incluse — gratuitement pour les particuliers.
              Si {c.name} ne répond pas à vos besoins de vérification ou de filtres énergétiques, voici comment
              AfficheServices se positionne.
            </p>
          </div>
        </div>

        <div className="mt-4">
          <ArticleMeta />
        </div>

        {/* TL;DR */}
        <section className="mt-6 rounded-2xl border border-primary-200 bg-primary-50/40 p-6">
          <h2 className="text-lg font-bold text-slate-900">En résumé</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            <strong>{c.name}</strong> est {c.positioning.toLowerCase()} <strong>AfficheServices</strong> se
            distingue par sa vérification d'identité KYC en plus du SIRET, son filtre RGE granulaire activable
            sur chaque catégorie, et sa médiation gratuite incluse en cas de litige. Les particuliers paient
            zéro, sans inscription ni engagement.
          </p>
        </section>

        {/* Quick comparison */}
        <section className="mt-8 overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-lg font-bold text-slate-900">Comparatif AfficheServices vs {c.name}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 sm:px-6">Critère</th>
                  <th className="px-4 py-3 text-center sm:px-6">AfficheServices</th>
                  <th className="px-4 py-3 text-center sm:px-6">{c.name}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <CompRow label="Année de création" left="2024" right={String(c.founded)} />
                <CompRow label="Artisans référencés" left={AFFICHESERVICES.proCount} right={c.proCount} />
                <CompRow label="Prix pour le particulier" left="100 % gratuit, sans inscription" right={c.pricingClient} />
                <CompRow label="Vérification SIRET" left="Auto via SIRENE (INSEE)" right={c.verificationLevels.find((v) => v.toLowerCase().includes('siret')) ?? 'Non documenté'} />
                <CompRow label="Vérification identité (KYC)" left="Oui, comptes Pro et Enterprise" right="Non documenté publiquement" />
                <CompRow label="Contrôle assurance RC Pro" left="Oui, vérifié" right="Non documenté publiquement" />
                <CompRow label="Filtre RGE / Qualibat" left="Oui, activable par catégorie" right={c.rgeFilter === 'native' ? 'Oui' : c.rgeFilter === 'limited' ? 'Limité ou non visible' : 'Non documenté'} />
                <CompRow label="Médiation gratuite en cas de litige" left="Oui, incluse" right="Non documentée publiquement" />
                <CompRow label="Devis comparatifs" left="3 devis" right={c.responseTime.includes('5 devis') ? '5 devis' : 'Variable'} />
                <CompRow label="Délai de réponse" left="48h standard, 4h urgence" right={c.responseTime} />
              </tbody>
            </table>
          </div>
        </section>

        {/* Why switch */}
        <section className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Pourquoi passer de {c.name} à AfficheServices ?</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {AFFICHESERVICES.differentiators.map((d, i) => (
              <div key={i} className="flex gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{d.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600">{d.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Honest about competitor strengths */}
        <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/40 p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Shield className="h-5 w-5 text-amber-600" /> Quand {c.name} reste un bon choix
          </h2>
          <p className="mt-2 text-sm text-slate-600">{c.whoItsFor}</p>
          <ul className="mt-3 space-y-1.5">
            {c.strengths.slice(0, 3).map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {s}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            Notre objectif n'est pas de critiquer {c.name} mais de vous aider à choisir l'outil
            adapté à votre projet. Si la notoriété historique compte plus que le filtre RGE, {c.name} reste
            une option valide.
          </p>
        </section>

        {/* Switching path */}
        <section className="mt-6 rounded-2xl border border-primary-200 bg-primary-50/30 p-6">
          <h2 className="text-lg font-bold text-slate-900">Comment passer à AfficheServices en 5 minutes</h2>
          <ol className="mt-4 space-y-3 text-sm text-slate-700">
            <li className="flex gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">1</span>
              <div>
                <strong>Décrivez votre projet en 2 minutes.</strong> Type de travaux, localisation, urgence, budget estimé.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">2</span>
              <div>
                <strong>Recevez 3 devis vérifiés sous 48h.</strong> Tous les artisans sont SIRET-vérifiés, RC Pro contrôlée, identité KYC pour les Pro.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">3</span>
              <div>
                <strong>Comparez et choisissez librement.</strong> Pas d'engagement, pas de frais. Médiation gratuite si désaccord.
              </div>
            </li>
          </ol>
          <Link
            to="/devis-gratuit"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
          >
            Demander 3 devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        {/* FAQ */}
        <section className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-4 space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="group border-b border-slate-100 pb-4 last:border-0">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900 hover:text-primary">
                  {f.question}
                  <span className="text-slate-300 group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Hub link */}
        <section className="mt-8 grid gap-3 sm:grid-cols-2">
          {Object.values(COMPETITORS).filter((other) => other.slug !== c.slug).map((other) => (
            <Link
              key={other.slug}
              to={`/alternatives/${other.slug}`}
              className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50"
            >
              <p className="text-xs font-semibold text-slate-500">Autre alternative</p>
              <p className="mt-1 text-sm font-bold text-slate-900">→ Alternative à {other.name}</p>
            </Link>
          ))}
        </section>
      </article>

      <Footer />
    </div>
  )
}

function CompRow({ label, left, right }: { label: string; left: string; right: string }) {
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-slate-700 sm:px-6">{label}</td>
      <td className="px-4 py-3 text-center text-emerald-700 sm:px-6">
        <span className="inline-flex items-center gap-1">
          <CheckCircle className="h-3.5 w-3.5" /> {left}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-slate-500 sm:px-6">{right}</td>
    </tr>
  )
}
